import { ArrowLeft, ClipboardCheck, Droplet, Edit, Loader2, Network, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { Skeleton } from '@/components/ui/skeleton';
import {
  useDeleteVessel,
  useVessel,
} from '@/features/vessels/queries';
import {
  vesselDisplayName,
  type Vessel,
} from '@/features/vessels/types';
import { ApiError } from '@/lib/api';
import { cn } from '@/lib/utils';
import { VesselDocumentsCard } from './VesselDocumentsCard';
import { VesselPhotoCard } from './VesselPhotoCard';
import { VesselReportsCard } from './VesselReportsCard';
import { VesselWarrantyCard } from './VesselWarrantyCard';

/**
 * Single field row used in every detail section. Centralises the
 * "label + value (or em-dash placeholder)" rendering so all fields
 * align consistently.
 */
function Field({
  label,
  value,
  fullWidth = false,
}: {
  label: string;
  value: React.ReactNode;
  fullWidth?: boolean;
}) {
  const { t } = useTranslation();
  const display =
    value === null || value === undefined || value === ''
      ? t('common.notSpecified', { defaultValue: '—' })
      : value;
  return (
    <div className={cn(fullWidth && 'sm:col-span-2')}>
      <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </dt>
      <dd className="mt-1 text-sm">{display}</dd>
    </div>
  );
}

/** Wrapper Card with a translated section title and a 2-col field grid. */
function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <dl className="grid gap-4 sm:grid-cols-2">{children}</dl>
      </CardContent>
    </Card>
  );
}

function DetailSkeleton() {
  return (
    <div className="space-y-4">
      <Skeleton className="h-9 w-64" />
      <Skeleton className="h-32 w-full" />
      <Skeleton className="h-32 w-full" />
    </div>
  );
}

function DetailContent({ vessel }: { vessel: Vessel }) {
  const { t } = useTranslation();
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Section title={t('vessel.detail.sectionIdentity')}>
        <Field label={t('vessel.fields.name')} value={vessel.name} />
        <Field label={t('vessel.fields.imo')} value={vessel.imo} />
        <Field label={t('vessel.fields.mmsi')} value={vessel.mmsi} />
        <Field label={t('vessel.fields.callSign')} value={vessel.callSign} />
        <Field label={t('vessel.fields.flag')} value={vessel.flag?.toUpperCase()} />
      </Section>

      <Section title={t('vessel.detail.sectionCategory')}>
        <Field
          label={t('vessel.fields.category')}
          value={t(`vessel.category.${vessel.category}`)}
        />
        <Field
          label={t('vessel.fields.sensitivityLevel')}
          value={t(`vessel.sensitivity.${vessel.sensitivityLevel}`)}
        />
        {vessel.category === 'armed' && (
          <>
            <Field label={t('vessel.fields.nsnOtan')} value={vessel.nsnOtan} />
            <Field
              label={t('vessel.fields.displayLabel')}
              value={vessel.displayLabel}
            />
          </>
        )}
      </Section>

      <Section title={t('vessel.detail.sectionPhysical')}>
        <Field label={t('vessel.fields.lengthM')} value={vessel.lengthM} />
        <Field label={t('vessel.fields.beamM')} value={vessel.beamM} />
        <Field label={t('vessel.fields.grossTonnage')} value={vessel.grossTonnage} />
        <Field label={t('vessel.fields.yearBuilt')} value={vessel.yearBuilt} />
        <Field label={t('vessel.fields.shipyard')} value={vessel.shipyard} fullWidth />
      </Section>

      <Section title={t('vessel.detail.sectionClassification')}>
        <Field
          label={t('vessel.fields.classSociety')}
          value={vessel.classSociety}
        />
        <Field label={t('vessel.fields.classNumber')} value={vessel.classNumber} />
        <Field
          label={t('vessel.fields.classSurveyed')}
          value={
            <span
              className={cn(
                'inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium',
                vessel.classSurveyed
                  ? 'bg-emerald-100 text-emerald-700'
                  : 'bg-muted text-muted-foreground',
              )}
            >
              {t(`common.${vessel.classSurveyed ? 'yes' : 'no'}`)}
            </span>
          }
        />
      </Section>

      <Section title={t('vessel.detail.sectionOperational')}>
        <Field label={t('vessel.fields.homePort')} value={vessel.homePort} />
        <Field
          label={t('vessel.fields.isActive')}
          value={
            <span
              className={cn(
                'inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium',
                vessel.isActive
                  ? 'bg-emerald-100 text-emerald-700'
                  : 'bg-muted text-muted-foreground',
              )}
            >
              {t(`vessel.status.${vessel.isActive ? 'active' : 'inactive'}`)}
            </span>
          }
        />
      </Section>
    </div>
  );
}

export function VesselDetailPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { data: vessel, isLoading, isError, error, refetch } = useVessel(id);
  const deleteMut = useDeleteVessel();
  const [confirmOpen, setConfirmOpen] = useState(false);

  const errorMessage = (() => {
    if (!isError) return null;
    if (error instanceof ApiError) {
      return t(error.key, {
        defaultValue: t('vessel.detail.loadError'),
        ...(error.params ?? {}),
      });
    }
    return t('vessel.detail.loadError');
  })();

  const onConfirmDelete = () => {
    if (!id) return;
    deleteMut.mutate(id, {
      onSuccess: () => {
        setConfirmOpen(false);
        navigate('/vessels');
      },
    });
  };

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Button asChild variant="ghost" size="icon" aria-label={t('common.back')}>
            <Link to="/vessels">
              <ArrowLeft aria-hidden />
            </Link>
          </Button>
          <h1 className="text-2xl font-semibold">
            {vessel ? vesselDisplayName(vessel) : t('common.loading')}
          </h1>
        </div>
        {vessel && (
          <div className="flex gap-2">
            <Button asChild variant="outline">
              <Link to={`/vessels/${vessel.id}/equipment`}>
                <Network aria-hidden />
                {t('equipment.tree.title')}
              </Link>
            </Button>
            <Button asChild variant="outline">
              <Link to={`/vessels/${vessel.id}/liquids`}>
                <Droplet aria-hidden />
                {t('liquids.nav.title')}
              </Link>
            </Button>
            <Button asChild variant="outline">
              <Link to={`/vessels/${vessel.id}/inspection-rounds`}>
                <ClipboardCheck aria-hidden />
                {t('vesselInspections.title')}
              </Link>
            </Button>
            <Button asChild variant="outline">
              <Link to={`/vessels/${vessel.id}/edit`}>
                <Edit aria-hidden />
                {t('common.edit')}
              </Link>
            </Button>
            <Button
              variant="destructive"
              onClick={() => setConfirmOpen(true)}
              disabled={deleteMut.isPending}
            >
              {deleteMut.isPending ? (
                <Loader2 className="animate-spin" aria-hidden />
              ) : (
                <Trash2 aria-hidden />
              )}
              {t('common.delete')}
            </Button>
          </div>
        )}
      </header>

      {errorMessage && (
        <Alert variant="destructive">
          <AlertTitle>{t('vessel.detail.loadError')}</AlertTitle>
          <AlertDescription className="space-y-2">
            <p>{errorMessage}</p>
            <Button variant="outline" size="sm" onClick={() => refetch()}>
              {t('common.retry')}
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {isLoading && <DetailSkeleton />}
      {vessel && (
        <>
          <DetailContent vessel={vessel} />
          <VesselWarrantyCard vesselId={vessel.id} />
          <VesselReportsCard vesselId={vessel.id} />
          <div className="grid gap-4 lg:grid-cols-2">
            <VesselPhotoCard vesselId={vessel.id} photoKey={vessel.photoKey} />
            <VesselDocumentsCard vesselId={vessel.id} />
          </div>
        </>
      )}

      {vessel && (
        <ConfirmDialog
          open={confirmOpen}
          onOpenChange={setConfirmOpen}
          title={t('vessel.delete.title')}
          description={t('vessel.delete.description', {
            name: vesselDisplayName(vessel),
          })}
          confirmLabel={t('vessel.delete.confirm')}
          variant="destructive"
          loading={deleteMut.isPending}
          onConfirm={onConfirmDelete}
        />
      )}
    </div>
  );
}
